import React, { useEffect, useRef, useState, useContext } from 'react';
import { Check, ChevronDown, LoaderCircle, MapPin, Search } from 'lucide-react';
import './LocationButton.css';
import { apiUrl } from '../../../config/api';
import { UserAuthContext } from '../../../UserAuthContext';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const LocationButton = ({ isMobile = false }) => {
  const { location, updateUserLocation, approximateLocationIssue } = useContext(UserAuthContext);
  const { currency, currencies, setCurrency } = useCurrency();
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [showLocationInput, setShowLocationInput] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);

  const formatLocation = (value) => {
    const parts = [value?.city, value?.region, value?.country]
      .filter(Boolean)
      .filter((part, index, values) => values.indexOf(part) === index);
    if (!parts.length) return 'Select Location';
    return `${parts.join(', ')}${value?.source === 'approximate' ? ' (approx.)' : ''}`;
  };

  const resetSearch = () => {
    setShowLocationInput(false);
    setLocationInput('');
    setSuggestions([]);
    setSelectedSuggestion(null);
    setLocationError('');
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsLocationMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    const query = locationInput.trim();
    if (!showLocationInput || query.length < 2) {
      setSuggestions([]);
      setSearching(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      setLocationError('');
      try {
        const response = await fetch(apiUrl(`/api/locations/search?q=${encodeURIComponent(query)}`), {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Location search failed');
        setSuggestions(Array.isArray(data.locations) ? data.locations : []);
        if (!data.locations?.length) setLocationError('No matching city, state, or country was found.');
      } catch (error) {
        if (error.name !== 'AbortError') setLocationError(error.message || 'Location suggestions are temporarily unavailable.');
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 450);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [locationInput, showLocationInput]);

  const saveLocation = (nextLocation) => {
    if (!nextLocation) return;
    updateUserLocation(nextLocation);
    resetSearch();
    setIsLocationMenuOpen(false);
  };

  const useCurrentLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Current location is not supported by this browser.');
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const endpoint = apiUrl(`/api/locations/reverse?latitude=${encodeURIComponent(coords.latitude)}&longitude=${encodeURIComponent(coords.longitude)}`);
        const response = await fetch(endpoint);
        const data = await response.json();
        if (!response.ok || !data.location) throw new Error(data.error || 'Current location could not be identified.');
        saveLocation(data.location);
      } catch (error) {
        setLocationError(error.message || 'Current location could not be identified.');
      } finally {
        setLocating(false);
      }
    }, (error) => {
      const denied = error.code === 1;
      setLocationError(denied
        ? 'Location permission was denied. Allow it in your browser or search manually.'
        : 'Current location is unavailable. Search for your city, state, or country instead.');
      setLocating(false);
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 300000 });
  };

  const chooseSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setLocationError('');
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveLocation(selectedSuggestion || suggestions[0]);
    }
    if (event.key === 'Escape') resetSearch();
  };

  const buttonClass = isMobile ? 'mobile-header-location-button' : 'header-location-button';

  return (
    <div className="header-location-button-container" ref={containerRef}>
      <button
        type="button"
        className={buttonClass}
        onClick={() => setIsLocationMenuOpen((open) => !open)}
        aria-expanded={isLocationMenuOpen}
        aria-haspopup="dialog"
      >
        <MapPin size={20} />
        <span className="location-text">{formatLocation(location)}</span>
        <ChevronDown size={16} className={`chevron ${isLocationMenuOpen ? 'open' : ''}`} />
        {isMobile && <ChevronDown size={16} className={`mobile-chevron ${isLocationMenuOpen ? 'open' : ''}`} />}
      </button>

      {isLocationMenuOpen && (
        <div className="location-dropdown" role="dialog" aria-label="Select Delivery Location" onPointerDown={(event) => event.stopPropagation()}>
          <div className="dropdown-content">
            <h3 className="dropdown-title">Select Delivery Location</h3>
            {location?.country && location.country !== 'Unknown' && (
              <p className="location-current"><MapPin size={14} /> {formatLocation(location)}</p>
            )}
            {location?.source === 'approximate' && (
              <p className="location-current">Estimated from your network. Search or use your device location to make it more precise.</p>
            )}
            {approximateLocationIssue && (
              <p className="location-limit-warning" role="status">{approximateLocationIssue}</p>
            )}
            <label className="location-currency">
              <span>{t('Display currency')}</span>
              <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                {currencies.map((code) => <option key={code} value={code}>{code}</option>)}
              </select>
            </label>

            {!showLocationInput ? (
              <div className="dropdown-options">
                <button type="button" onClick={() => { setShowLocationInput(true); setLocationError(''); }} className="dropdown-option">
                  <span className="option-content"><Search size={20} /><span>Search Location</span></span>
                </button>
                <button type="button" onClick={useCurrentLocation} className="dropdown-option share" disabled={locating}>
                  <span className="option-content">
                    {locating ? <LoaderCircle size={20} className="location-spinner" /> : <MapPin size={20} />}
                    <span>{locating ? 'Finding your location…' : 'Use my current location'}</span>
                  </span>
                </button>
                {locationError && <p className="location-error" role="alert">{locationError}</p>}
              </div>
            ) : (
              <div className="input-section">
                <div className="input-container">
                  {searching ? <LoaderCircle size={20} className="location-spinner" /> : <Search size={20} />}
                  <input
                    type="search"
                    placeholder="City, state or country"
                    value={locationInput}
                    onChange={(event) => {
                      setLocationInput(event.target.value);
                      setSelectedSuggestion(null);
                    }}
                    className="location-input"
                    onKeyDown={handleInputKeyDown}
                    autoComplete="off"
                    autoFocus
                    aria-label="Search for a city, state, or country"
                  />
                </div>

                {suggestions.length > 0 && (
                  <div className="location-suggestions" role="listbox" aria-label="Physical location suggestions">
                    {suggestions.map((suggestion) => {
                      const selected = selectedSuggestion?.id === suggestion.id;
                      return (
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          key={suggestion.id}
                          className={`location-suggestion ${selected ? 'selected' : ''}`}
                          onClick={() => chooseSuggestion(suggestion)}
                        >
                          <MapPin size={16} />
                          <span>{suggestion.label}</span>
                          {selected && <Check size={17} />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {locationError && <p className="location-error" role="alert">{locationError}</p>}
                <p className="location-attribution">Locations © OpenStreetMap contributors</p>
                <div className="button-group">
                  <button type="button" onClick={() => saveLocation(selectedSuggestion)} className="location-confirm-button" disabled={!selectedSuggestion}>
                    Save location
                  </button>
                  <button type="button" onClick={resetSearch} className="location-cancel-button">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationButton;
